import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { getHljs } from '../utils/highlightLoader';
import { MarkdownContent } from '../shared-ui/MarkdownContent';
import type { TtsDocSegment } from '../shared-ui/MarkdownContent';
import { DiffView } from '../shared-ui/DiffView';
import { useIsDesktop } from '../shared-ui/useIsDesktop';
import { dirname } from '../utils/resolveMarkdownHref';
import { DocTtsPlaybackBar } from './DocTtsPlaybackBar';
import { useDocTtsStore, useDocTtsEntry } from '../state/docTtsStore';

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp', 'avif']);
const VIDEO_EXTS = new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'ogv']);

/**
 * Rewrite relative markdown image paths to the /api/files/media endpoint so
 * that images like `![alt](images/foo.png)` in a file at
 * `calendar/_operational_docs/doc.md` resolve to
 * `/api/files/media?path=calendar/_operational_docs/images/foo.png`
 * instead of the PWA origin (which would 404).
 */
function resolveMarkdownImages(text: string, filePath: string): string {
  const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
  return text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt, src: string) => {
    if (/^(https?:\/\/|\/|data:)/.test(src)) return _match; // already absolute
    const resolved = dir ? `${dir}/${src}` : src;
    return `![${alt}](/api/files/media?path=${encodeURIComponent(resolved)})`;
  });
}

function fileExt(path: string): string {
  return (path.split('.').pop() ?? '').toLowerCase();
}

interface FileData {
  path: string;
  fileName: string;
  language: string;
  totalLines: number;
  startLine: number;
  endLine: number;
  targetLine: number | null;
  lines: string[];
  hasDiff?: boolean;
}

interface DiffData {
  status: 'modified' | 'new' | 'deleted' | 'unchanged' | 'no-repo' | 'binary' | 'last-commit';
  baseBranch: string;
  hunks?: Array<{
    oldStart: number; oldLines: number; newStart: number; newLines: number;
    lines: Array<{ type: 'add' | 'remove' | 'context'; content: string }>;
  }>;
  rawDiff?: string;
  stats?: { added: number; removed: number };
  lastCommitRef?: string;
}

// Lazy-loaded Monaco component — only imported on desktop
let MonacoViewerPromise: Promise<{ MonacoViewer: any }> | null = null;
function loadMonacoViewer() {
  if (!MonacoViewerPromise) {
    MonacoViewerPromise = import('./MonacoViewer');
  }
  return MonacoViewerPromise;
}

// `useIsDesktop` is now the canonical shared hook in `shared-ui/useIsDesktop.ts`
// (Phase 0b migration). It adds `pointer: fine` + a persisted manual override to the
// former bare `min-width: 900px` query, so a ≥900px *touch* device now renders the
// mobile (hljs) viewer rather than Monaco — intended (Fork E, locked).

/**
 * Code Viewer — slide-in panel for viewing file contents.
 * Desktop (>=900px): Monaco Editor with edit capability.
 * Mobile: highlight.js powered read-only viewer with line numbers.
 */
export function CodeViewer() {
  const [file, setFile] = useState<FileData | null>(null);
  const [mediaPath, setMediaPath] = useState<string | null>(null); // image/video path
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedLines, setHighlightedLines] = useState<string[]>([]);
  const [MonacoComp, setMonacoComp] = useState<any>(null);
  const [fullContent, setFullContent] = useState<string | undefined>(undefined);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [mdPreview, setMdPreview] = useState(true); // markdown files default to preview
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState<DiffData | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareToast, setShareToast] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const fileHashRef = useRef<string>('');

  // TTS state — linked to docTtsStore (a SINGLETON shared with chat-message
  // read-aloud). CodeViewer must only react to / drive the store when the active
  // read targets a DOC source — otherwise opening a markdown file mid-chat-read
  // would clobber the chat's segments (singleton store) and render a duplicate
  // playback bar. `isTtsActive` therefore also checks `source?.kind === 'doc'`.
  const { phase: ttsPhase, source: ttsSource, currentSegment: ttsCurrentSegment, unlockAudio, startReading, stop: ttsStop, seekTo: ttsSeekTo } = useDocTtsEntry();
  const setTtsSegments = useDocTtsStore((s) => s.setSegments);
  const isTtsActive = ttsPhase !== 'idle' && ttsPhase !== 'stopped' && ttsSource?.kind === 'doc';
  const isMarkdownFile = !!file && file.language === 'markdown';

  const handleReadAloud = useCallback(() => {
    if (!file || !isMarkdownFile) return;
    if (isTtsActive) {
      ttsStop();
    } else {
      // Unlock the audio element INSIDE this click gesture — the first segment
      // plays only after the WS round-trip (outside the gesture), which mobile
      // browsers would otherwise block. Must run before startReading().
      unlockAudio();
      startReading({ kind: 'doc', fileId: file.path });
    }
  }, [file, isMarkdownFile, isTtsActive, ttsStop, startReading, unlockAudio]);

  const handleSegmentsReady = useCallback((segments: TtsDocSegment[]) => {
    setTtsSegments(segments);
  }, [setTtsSegments]);

  // Stop TTS when file changes — but only a DOC read (the chat-message read on
  // the same singleton store must survive opening/switching a file in the viewer).
  useEffect(() => {
    if (useDocTtsStore.getState().source?.kind === 'doc') ttsStop();
  }, [file?.path]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDesktop = useIsDesktop();

  // Preload Monaco on desktop
  useEffect(() => {
    if (isDesktop && !MonacoComp) {
      loadMonacoViewer().then((mod) => setMonacoComp(() => mod.MonacoViewer));
    }
  }, [isDesktop]);

  // Single exit point: step one entry back in the browser history. The popstate
  // handler below reads the resulting hash and either rehydrates the previous
  // file in the viewing stack (breadcrumb pop) or clears the viewer if the
  // pre-viewer history entry is now at the top.
  const close = useCallback(() => {
    window.history.back();
  }, []);

  // Listen for open-file-viewer events
  useEffect(() => {
    const handler = async (e: Event) => {
      const { path, line, _fromPopstate } = (e as CustomEvent).detail;
      setLoading(true);
      setError(null);
      setHighlightedLines([]);
      setFullContent(undefined);
      setSaveMsg(null);
      setMdPreview(true);
      setShowDiff(false);
      setDiffData(null);
      setMediaPath(null);
      setMediaType(null);

      // Push a new history entry per file open so the stack acts as a breadcrumb
      // trail. Skip when we're reacting to a popstate (already at this hash) or
      // reopening the same file (no new step in the trail).
      const newHash = `#file:${encodeURIComponent(path)}`;
      if (!_fromPopstate && window.location.hash !== newHash) {
        window.history.pushState({ codeViewer: true }, '', newHash);
      }

      const ext = fileExt(path);

      // Media files: render directly via /api/files/media, skip text read
      if (IMAGE_EXTS.has(ext)) {
        setMediaPath(path);
        setMediaType('image');
        setLoading(false);
        return;
      }
      if (VIDEO_EXTS.has(ext)) {
        setMediaPath(path);
        setMediaType('video');
        setLoading(false);
        return;
      }

      try {
        const params = new URLSearchParams({ path });
        if (line) params.set('line', String(line));
        // HTML files need full content for the iframe preview to render correctly
        if (ext === 'html' || ext === 'htm') params.set('full', '1');
        const res = await authFetch(`/api/files/read?${params}`);
        if (!res.ok) {
          const msg = await res.text();
          throw new Error(msg || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setFile(data);
        if (data.hash) fileHashRef.current = data.hash;
      } catch (err: any) {
        setError(err.message || 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('open-file-viewer', handler);
    return () => window.removeEventListener('open-file-viewer', handler);
  }, []);

  // Apply highlight.js on mobile (or while Monaco loads on desktop)
  useEffect(() => {
    if (!file || (isDesktop && MonacoComp)) return;
    let cancelled = false;

    getHljs().then((hljs) => {
      if (cancelled) return;
      const lang = file.language;
      const hasLang = lang && hljs.getLanguage(lang);

      const highlighted = file.lines.map((line) => {
        if (!line.trim()) return '';
        try {
          if (hasLang) return hljs.highlight(line, { language: lang }).value;
          return escapeHtml(line);
        } catch {
          return escapeHtml(line);
        }
      });
      setHighlightedLines(highlighted);
    });

    return () => { cancelled = true; };
  }, [file, isDesktop, MonacoComp]);

  // Breadcrumb navigation: on browser back / swipe / header-back, inspect the
  // resulting hash. `#file:<encoded>` → rehydrate that file (one step back in
  // the viewing trail). Anything else → clear viewer state entirely.
  useEffect(() => {
    const onPop = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#file:')) {
        try {
          const path = decodeURIComponent(hash.slice('#file:'.length));
          if (path) {
            window.dispatchEvent(new CustomEvent('open-file-viewer', {
              detail: { path, _fromPopstate: true },
            }));
            return;
          }
        } catch {
          // malformed hash — fall through to clear
        }
      }
      setFile(null);
      setMediaPath(null);
      setMediaType(null);
      setError(null);
      setHighlightedLines([]);
      setFullContent(undefined);
      setSaveMsg(null);
      setShowDiff(false);
      setDiffData(null);
      fileHashRef.current = '';
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!file && !loading && !error) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [file, loading, error, close]);

  // Scroll to target line (mobile only — Monaco handles its own scrolling)
  useEffect(() => {
    if (isDesktop && MonacoComp) return;
    if (file?.targetLine && targetRef.current) {
      targetRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [file, highlightedLines, isDesktop, MonacoComp]);

  // Save handler for Monaco
  const handleSave = useCallback(async (content: string) => {
    if (!file) return;
    setSaveMsg(null);
    const res = await authFetch('/api/files/write', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path: file.path,
        content,
        lastHash: fileHashRef.current || undefined,
      }),
    });
    if (!res.ok) {
      const body = await res.text();
      if (res.status === 409) {
        throw new Error('File changed on disk. Close and reopen to get latest version.');
      }
      throw new Error(body || `Save failed: HTTP ${res.status}`);
    }
    const result = await res.json();
    if (result.hash) fileHashRef.current = result.hash;
    setSaveMsg('Saved');
    setTimeout(() => setSaveMsg(null), 2000);
  }, [file]);

  // Load full file for editing
  const loadFullFile = useCallback(async () => {
    if (!file) return;
    const params = new URLSearchParams({ path: file.path, full: '1' });
    const res = await authFetch(`/api/files/read?${params}`);
    if (!res.ok) return;
    const data = await res.json();
    setFullContent(data.lines.join('\n'));
    if (data.hash) fileHashRef.current = data.hash;
    setFile((prev) => prev ? { ...prev, startLine: 1, endLine: data.totalLines, lines: data.lines, totalLines: data.totalLines } : prev);
  }, [file]);

  const handleDownloadPdf = useCallback(async () => {
    if (!file || pdfLoading) return;
    setPdfLoading(true);
    try {
      const res = await authFetch(`/api/files/pdf?path=${encodeURIComponent(file.path)}&includeImages=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName.replace(/\.(md|html?)$/i, '') + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setShareToast('PDF generation failed');
      setTimeout(() => setShareToast(null), 3000);
    } finally {
      setPdfLoading(false);
    }
  }, [file, pdfLoading]);

  const handleShare = useCallback(async () => {
    if (!file) return;
    const filePath = file.path;
    const pageUrl = `${window.location.origin}/#file:${encodeURIComponent(filePath)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: file.fileName, text: filePath, url: pageUrl });
        return;
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(filePath);
      setShareToast('Path copied');
    } catch {
      setShareToast('Copy failed');
    }
    setTimeout(() => setShareToast(null), 2000);
  }, [file]);

  const handleDiffToggle = useCallback(async () => {
    if (showDiff) {
      setShowDiff(false);
      return;
    }
    if (diffData) {
      setShowDiff(true);
      return;
    }
    if (!file) return;
    setDiffLoading(true);
    try {
      const res = await authFetch(`/api/files/diff?path=${encodeURIComponent(file.path)}&base=main`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: DiffData = await res.json();
      setDiffData(data);
      setShowDiff(true);
    } catch {
      // silently fail — diff tab just won't show content
    } finally {
      setDiffLoading(false);
    }
  }, [file, showDiff, diffData]);

  const isOpen = !!(file || loading || error || mediaPath);
  if (!isOpen) return null;

  const useMonaco = isDesktop && MonacoComp && file;
  const mediaFileName = mediaPath ? mediaPath.split('/').pop() ?? mediaPath : '';

  return (
    <div class="code-viewer-overlay" onClick={close}>
      <div class="code-viewer-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div class="code-viewer-header">
          <button class="code-viewer-back" onClick={close} title="Close (Esc)">
            ← Back
          </button>
          <span class="code-viewer-filename">
            {file?.fileName || mediaFileName || 'Loading...'}
          </span>
          {file?.targetLine && (
            <span class="code-viewer-line-info">line {file.targetLine}</span>
          )}
          {file?.language && (
            <span class="code-viewer-lang-badge">{file.language}</span>
          )}
          {(file?.language === 'markdown' || file?.language === 'html' || file?.language === 'htm') && !showDiff && (
            <button
              class={`code-viewer-md-toggle ${mdPreview ? 'active' : ''}`}
              onClick={() => setMdPreview((p) => !p)}
              title={mdPreview ? 'Switch to raw source' : 'Switch to preview'}
            >
              {mdPreview ? 'Source' : 'Preview'}
            </button>
          )}
          {file?.hasDiff && (
            <button
              class={`code-viewer-diff-toggle ${showDiff ? 'active' : ''}`}
              onClick={handleDiffToggle}
              title={showDiff ? 'Show file' : diffData?.status === 'last-commit' ? 'Show last commit diff' : 'Show diff vs main'}
              disabled={diffLoading}
            >
              {diffLoading ? '…' : showDiff ? 'File' : diffData?.status === 'last-commit' ? 'Last commit' : 'Diff'}
            </button>
          )}
          {isMarkdownFile && mdPreview && !showDiff && !useMonaco && (
            <button
              class={`code-viewer-tts-btn${isTtsActive ? ' active' : ''}`}
              onClick={handleReadAloud}
              title={isTtsActive ? 'Stop reading' : 'Read aloud'}
            >
              {ttsPhase === 'loading' ? '…' : ttsPhase === 'playing' ? '⏸' : ttsPhase === 'paused' ? '▶' : '🔊'}
            </button>
          )}
          {file && (file.language === 'markdown' || file.language === 'html' || file.language === 'htm') && (
            <button
              class="code-viewer-pdf-btn"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              title="Download as PDF"
            >
              {pdfLoading ? '…' : '⬇ PDF'}
            </button>
          )}
          {file && (
            <button
              class="code-viewer-share-btn"
              onClick={handleShare}
              title="Share / copy path"
            >
              ↗
            </button>
          )}
          {shareToast && <span class="code-viewer-save-msg">{shareToast}</span>}
          {saveMsg && <span class="code-viewer-save-msg">{saveMsg}</span>}
          {file && file.startLine > 1 && (
            <button class="code-viewer-load-full" onClick={loadFullFile} title="Load entire file">
              Load full file
            </button>
          )}
          {file && (
            <span class="code-viewer-path-full" title={file.path}>
              {file.path}
            </span>
          )}
        </div>

        {/* Content */}
        <div class="code-viewer-content" ref={scrollRef}>
          {loading && (
            <div class="code-viewer-loading">
              <span class="spinner" style={{ width: 18, height: 18 }} /> Loading...
            </div>
          )}
          {error && (
            <div class="code-viewer-error">{error}</div>
          )}

          {/* Media preview — image or video */}
          {mediaPath && mediaType === 'image' && (
            <div class="code-viewer-media">
              <img
                src={`/api/files/media?path=${encodeURIComponent(mediaPath)}`}
                alt={mediaFileName}
                class="code-viewer-media-img"
              />
            </div>
          )}
          {mediaPath && mediaType === 'video' && (
            <div class="code-viewer-media">
              <video
                src={`/api/files/media?path=${encodeURIComponent(mediaPath)}`}
                controls
                class="code-viewer-media-video"
              />
            </div>
          )}

          {/* Diff view (mobile table) */}
          {showDiff && diffData && diffData.hunks && (
            <DiffView hunks={diffData.hunks} stats={diffData.stats ?? { added: 0, removed: 0 }} baseBranch={diffData.baseBranch} lastCommitRef={diffData.lastCommitRef} />
          )}
          {showDiff && diffData && !diffData.hunks && (
            <div class="code-viewer-loading" style={{ color: 'var(--text-secondary)' }}>
              {diffData.status === 'unchanged' ? 'No changes vs main.' : `Diff unavailable (${diffData.status}).`}
            </div>
          )}

          {/* Desktop: Monaco Editor */}
          {!showDiff && useMonaco && (
            <MonacoComp
              file={file}
              fullContent={fullContent}
              onSave={handleSave}
            />
          )}

          {/* Markdown preview (mobile + desktop when not in Monaco edit mode) */}
          {!showDiff && file && !useMonaco && file.language === 'markdown' && mdPreview && (
            <div class="code-viewer-md-preview">
              <MarkdownContent
                text={resolveMarkdownImages(file.lines.join('\n'), file.path)}
                basePath={dirname(file.path)}
                ttsMode={isTtsActive}
                activeTtsSegment={isTtsActive ? ttsCurrentSegment : undefined}
                onTtsSegmentClick={isTtsActive ? ttsSeekTo : undefined}
                onSegmentsReady={handleSegmentsReady}
              />
              {isTtsActive && <DocTtsPlaybackBar onClose={ttsStop} />}
            </div>
          )}

          {/* HTML preview — sandboxed iframe for .html/.htm files */}
          {!showDiff && file && !useMonaco && (file.language === 'html' || file.language === 'htm') && mdPreview && (
            <iframe
              class="code-viewer-html-preview"
              srcdoc={file.lines.join('\n')}
              sandbox="allow-scripts"
              title="HTML Preview"
            />
          )}

          {/* Mobile (or fallback): highlight.js line table — all non-markdown/html files, or in source mode */}
          {!showDiff && file && !useMonaco && !((file.language === 'markdown' || file.language === 'html' || file.language === 'htm') && mdPreview) && (
            <table class="code-viewer-lines">
              <tbody>
                {file.lines.map((line, i) => {
                  const lineNum = file.startLine + i;
                  const isTarget = lineNum === file.targetLine;
                  const html = highlightedLines[i];
                  return (
                    <tr
                      key={lineNum}
                      class={isTarget ? 'code-viewer-line--target' : ''}
                      ref={isTarget ? targetRef as any : undefined}
                    >
                      <td class="code-viewer-gutter">{lineNum}</td>
                      <td class="code-viewer-code">
                        <pre><code
                          class={file.language ? `hljs language-${file.language}` : 'hljs'}
                          dangerouslySetInnerHTML={html != null ? { __html: html || '\n' } : undefined}
                        >{html == null ? line : undefined}</code></pre>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!showDiff && file && !useMonaco && file.startLine > 1 && (
            <div class="code-viewer-truncated">
              Showing lines {file.startLine}–{file.endLine} of {file.totalLines}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
